import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";
import { CommunicationCategory } from "@api/shared-runtime/communication-category";
import { CommunicationMethod } from "@api/shared-runtime/communication-method";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useTRPC } from "@/trpc";

const DISMISSED_KEY_PREFIX = "psv-monitoring-digest-banner-dismissed:";
const DISMISSED_VALUE = "1";

function readDismissed(organizationBusinessUnitId: string): boolean {
  try {
    return (
      localStorage.getItem(
        DISMISSED_KEY_PREFIX + organizationBusinessUnitId,
      ) === DISMISSED_VALUE
    );
  } catch {
    return false;
  }
}

function writeDismissed(organizationBusinessUnitId: string) {
  try {
    localStorage.setItem(
      DISMISSED_KEY_PREFIX + organizationBusinessUnitId,
      DISMISSED_VALUE,
    );
  } catch {
    // Storage can be blocked; the banner then reappears next visit.
  }
}

/** Offers the monthly results email to staff who can receive it and have not
 * turned it on. Hidden once subscribed, or dismissed on this device. */
export function DigestSubscribeBanner({
  organizationBusinessUnitId,
}: {
  organizationBusinessUnitId: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(() =>
    readDismissed(organizationBusinessUnitId),
  );
  const preferencesOptions =
    trpc.user.listOrgNotificationPreferences.queryOptions({
      organizationBusinessUnitId,
    });
  const preferences = useQuery(preferencesOptions);
  const subscribe = useMutation(
    trpc.user.setOrgNotificationPreference.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(preferencesOptions);
        toast.success("You'll get an email when monthly monitoring finishes.");
      },
      onError: () => {
        toast.error("Couldn't turn on the email");
      },
    }),
  );

  const category = preferences.data?.categories.find(
    (candidate) =>
      candidate.categoryId === CommunicationCategory.ORG_PSV_MONITORING_DIGEST,
  );
  const emailChannel = category?.channels.find(
    (channel) => channel.method === CommunicationMethod.EMAIL,
  );
  if (!category || emailChannel?.enabled || dismissed) {
    return null;
  }

  function dismiss() {
    writeDismissed(organizationBusinessUnitId);
    setDismissed(true);
  }

  return (
    <Card className="flex flex-col gap-3 border-blue-200 bg-blue-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-800">
          <Mail className="size-5" aria-hidden />
        </span>
        <div>
          <h2 className="font-semibold text-slate-900">
            Get an email when monthly monitoring finishes
          </h2>
          <p className="text-sm text-slate-600">
            We'll list providers whose results need review.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          disabled={subscribe.isPending}
          onClick={() =>
            subscribe.mutate({
              organizationBusinessUnitId,
              categoryId: CommunicationCategory.ORG_PSV_MONITORING_DIGEST,
              method: CommunicationMethod.EMAIL,
              enabled: true,
            })
          }
        >
          {subscribe.isPending ? "Turning on..." : "Email me"}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss"
          onClick={dismiss}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
    </Card>
  );
}
